import { useCallback, useContext, useMemo, useState } from "react";
import {
  AI_FOUNDATIONS_ENABLEMENT,
  hasNGCEnablement,
} from "utils/ngcEnablement";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import { OrgRoleIds } from "models/Role.model";
import type { NGCAPIKeySpec } from "server";
import {
  NGC_API_KEY_CREATION_ERROR_MESSAGE,
  type NgcApiKeyDeployResult,
} from "../configuration/ngc-api-key/NgcApiKeyOption";
import { useNgcApiKeyAccess } from "./useNgcApiKeyAccess";
import { getOrganizationRoleForOrg } from "utils/organizationAccess";

type DeployHandler = (
  ngcApiKeySpec?: NGCAPIKeySpec | null,
) => Promise<NgcApiKeyDeployResult | void> | NgcApiKeyDeployResult | void;

interface UseNgcApiKeyDeployOptionOptions {
  isPublic?: boolean;
  loading: boolean;
  initLoading: boolean;
  ngcApiKeySpec?: NGCAPIKeySpec | null;
  onDeploy: DeployHandler;
}

export const useNgcApiKeyDeployOption = ({
  isPublic,
  loading,
  initLoading,
  ngcApiKeySpec,
  onDeploy,
}: UseNgcApiKeyDeployOptionOptions) => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const [createNgcApiKey, setCreateNgcApiKey] = useState(false);
  const [
    isDeployWithoutNgcApiKeyConfirmationOpen,
    setIsDeployWithoutNgcApiKeyConfirmationOpen,
  ] = useState(false);
  const [ngcApiKeyErrorMessage, setNgcApiKeyErrorMessage] = useState<
    string | null
  >(null);
  const effectiveNgcApiKeySpec = ngcApiKeySpec ?? null;

  const activeOrg = orgContext.activeOrg;
  const resolvedActiveOrgId = activeOrg?.id ?? orgContext.activeOrgId;
  const activeOrgHasAIEnablement = hasNGCEnablement(
    activeOrg,
    AI_FOUNDATIONS_ENABLEMENT,
  );
  const activeOrgHasNgcOrgIdWithoutAIEnablement =
    !!activeOrg?.ngcOrgId && !activeOrgHasAIEnablement;
  const ownedOrgIds = useMemo(
    () =>
      new Set(
        userContext.me?.organizationAccesses
          ?.filter(
            (access) =>
              getOrganizationRoleForOrg([access], access.organizationId) ===
              OrgRoleIds.Owner,
          )
          .map((access) => access.organizationId) ?? [],
      ),
    [userContext.me?.organizationAccesses],
  );
  const ownedOrgIdsWithNgcApiKeyEnablement = useMemo(
    () =>
      (orgContext.orgs ?? [])
        .filter(
          (org) =>
            org.id !== resolvedActiveOrgId &&
            ownedOrgIds.has(org.id) &&
            hasNGCEnablement(org, AI_FOUNDATIONS_ENABLEMENT),
        )
        .map((org) => org.id),
    [resolvedActiveOrgId, orgContext.orgs, ownedOrgIds],
  );
  const shouldBlockDueToOtherAIEnabledOrg =
    !activeOrgHasAIEnablement && ownedOrgIdsWithNgcApiKeyEnablement.length > 0;
  const accessCheckEnabled =
    !isPublic &&
    !!effectiveNgcApiKeySpec &&
    !activeOrgHasNgcOrgIdWithoutAIEnablement &&
    !shouldBlockDueToOtherAIEnabledOrg;
  const accessStatus = useNgcApiKeyAccess({
    enabled: accessCheckEnabled,
    email: userContext.me?.email,
    activeOrgRole: orgContext.activeOrgRole,
    activeOrgHasNgcOrgIdWithoutAIEnablement,
    shouldBlockDueToOtherAIEnabledOrg,
  });

  const onCheckedChange = useCallback((checked: boolean) => {
    setCreateNgcApiKey(checked);
    if (checked) {
      setNgcApiKeyErrorMessage(null);
    }
  }, []);

  const canGenerateNgcApiKey =
    !isPublic &&
    !loading &&
    !!effectiveNgcApiKeySpec &&
    !activeOrgHasNgcOrgIdWithoutAIEnablement &&
    !shouldBlockDueToOtherAIEnabledOrg &&
    accessStatus === "available";

  const deployWithNgcApiKeyChoice = useCallback(
    async (shouldCreateNgcApiKey: boolean) => {
      const result = await onDeploy(
        shouldCreateNgcApiKey ? effectiveNgcApiKeySpec : null,
      );
      if (result?.ngcApiKeySpecFailed) {
        setCreateNgcApiKey(false);
        setNgcApiKeyErrorMessage(NGC_API_KEY_CREATION_ERROR_MESSAGE);
      }
    },
    [effectiveNgcApiKeySpec, onDeploy],
  );

  const handleDeploy = useCallback(async () => {
    const shouldCreateNgcApiKey =
      canGenerateNgcApiKey && createNgcApiKey === true;

    if (canGenerateNgcApiKey && !shouldCreateNgcApiKey) {
      setIsDeployWithoutNgcApiKeyConfirmationOpen(true);
      return;
    }

    await deployWithNgcApiKeyChoice(shouldCreateNgcApiKey);
  }, [canGenerateNgcApiKey, createNgcApiKey, deployWithNgcApiKeyChoice]);

  const handleConfirmDeployWithoutNgcApiKey = useCallback(async () => {
    setIsDeployWithoutNgcApiKeyConfirmationOpen(false);
    await deployWithNgcApiKeyChoice(false);
  }, [deployWithNgcApiKeyChoice]);

  const handleIncludeNgcApiKeyAndDeploy = useCallback(async () => {
    setCreateNgcApiKey(true);
    setNgcApiKeyErrorMessage(null);
    setIsDeployWithoutNgcApiKeyConfirmationOpen(false);
    await deployWithNgcApiKeyChoice(true);
  }, [deployWithNgcApiKeyChoice]);

  return {
    checked: canGenerateNgcApiKey && createNgcApiKey === true,
    errorMessage: canGenerateNgcApiKey ? ngcApiKeyErrorMessage : null,
    isDeployWithoutNgcApiKeyConfirmationOpen,
    isAiPlaygroundsKey: effectiveNgcApiKeySpec?.type === "AI_PLAYGROUNDS_KEY",
    isDisabled: initLoading,
    onConfirmDeployWithoutNgcApiKey: handleConfirmDeployWithoutNgcApiKey,
    onCheckedChange,
    onDeploy: handleDeploy,
    onIncludeNgcApiKeyAndDeploy: handleIncludeNgcApiKeyAndDeploy,
    onDeployWithoutNgcApiKeyConfirmationOpenChange:
      setIsDeployWithoutNgcApiKeyConfirmationOpen,
    shouldShowSection: canGenerateNgcApiKey,
  };
};
